import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as THREE from 'three';
import { updateWire } from '../../../redux/wireSlice';

/**
 * useFixtureGizmo - Aydınlatma fixture hatları için spline gizmo sistemi
 *
 * - Spline kontrol noktalarında gizmolar oluşturur
 * - Spline'a tıklandığında gizmoları gösterir
 * - Gizmoları sürükleyerek spline şeklini değiştirir
 */
export function useFixtureGizmo(scene, camera, domElement) {
    const dispatch = useDispatch();
    const settings = useSelector((state) => state.settings); // ✅ Settings'i al
    const selectedWireRef = useRef(null); // Seçili spline wire
    const gizmosRef = useRef([]); // Aktif gizmolar
    const selectedGizmo = useRef(null);
    const isMoving = useRef(false);
    const originalPosition = useRef(new THREE.Vector3());
    const originalControlPoints = useRef([]); // ESC için orijinal noktalar
    const raycaster = new THREE.Raycaster();
    const plane = new THREE.Plane();
    const offset = new THREE.Vector3();
    const planeIntersect = new THREE.Vector3();

    useEffect(() => {
        if (!scene || !camera || !domElement) {
            return;
        }

        const getMouse = (event) => {
            const rect = domElement.getBoundingClientRect();
            return {
                x: ((event.clientX - rect.left) / rect.width) * 2 - 1,
                y: -((event.clientY - rect.top) / rect.height) * 2 + 1,
            };
        };

        /**
         * Spline wire için gizmolar oluştur
         */
        const createGizmosForWire = (wireGroup) => {
            // Önce mevcut gizmoları temizle
            clearGizmos();

            // Wire group içindeki Line objesini bul
            let wireLine = null;
            wireGroup.traverse(obj => {
                if (obj.isLine && obj.userData?.type === 'curvedLine') {
                    wireLine = obj;
                }
            });

            if (!wireLine) {
                return;
            }

            // ✅ Wire'ın ID'sini line object'inden al (group'tan değil!)
            const wireId = wireLine.userData?.id;

            let controlPoints = [];

            // ✅ SADECE wireLine'ın kendi userData'sından vertices'i al (scene traverse yok!)
            if (wireLine.userData?.vertices && wireLine.userData.vertices.length > 0) {
                controlPoints = wireLine.userData.vertices.map(v =>
                    new THREE.Vector3(v.x, v.y, v.z || 0)
                );
            } else {
                // ✅ Vertices yoksa hata mesajı ver - fixture spline için vertices olmalı!
                console.error('❌ Fixture spline vertices bulunamadı! userData:', wireLine.userData);
                return;
            }

            if (controlPoints.length === 0) {
                return;
            }

            // Orijinal kontrol noktalarını kaydet (ESC için)
            originalControlPoints.current = controlPoints.map(p => p.clone());

            // Her kontrol noktası için gizmo oluştur (kare şeklinde, mavi)
            controlPoints.forEach((point, index) => {
                const gizmoGeometry = new THREE.BoxGeometry(5, 5, 5); // Kare şeklinde
                const gizmoMaterial = new THREE.MeshBasicMaterial({
                    color: 0x0000ff, // Mavi
                    opacity: 0.7,
                    transparent: true
                });
                const gizmo = new THREE.Mesh(gizmoGeometry, gizmoMaterial);

                gizmo.position.copy(point);
                gizmo.name = `__fixture_control_${index}`; // ✅ useGizmo'dan bağımsız isim
                gizmo.userData = {
                    type: 'splineControl',
                    wireId: wireId,
                    controlIndex: index,
                    wireGroup: wireGroup,
                    isFixtureGizmo: true // ✅ Fixture gizmo flag
                };

                scene.add(gizmo);
                gizmosRef.current.push(gizmo);
            });

            selectedWireRef.current = wireGroup;
        };

        /**
         * Gizmoları temizle
         */
        const clearGizmos = () => {
            gizmosRef.current.forEach(gizmo => {
                scene.remove(gizmo);
                gizmo.geometry?.dispose();
                gizmo.material?.dispose();
            });
            gizmosRef.current = [];
            selectedWireRef.current = null;
            originalControlPoints.current = [];
        };

        /**
         * Wire marker'ları temizle (gizmo sürüklenirken)
         */
        const clearWireMarkers = (wireId) => {
            const markersToRemove = [];
            scene.traverse(obj => {
                if (obj.userData?.role === 'wireMarker' &&
                    obj.userData?.parentWireId === wireId) {
                    markersToRemove.push(obj);
                }
            });

            markersToRemove.forEach(marker => {
                scene.remove(marker);
                marker.geometry?.dispose();
                marker.material?.dispose();
            });
        };

        /**
         * Wire marker'ları yeniden oluştur (gizmo sürükleme sonrası)
         */
        const recreateWireMarkers = (wireId, curve) => {
            // Vector döndürme helper fonksiyonu
            const rotateVector = (vector, angleRad) => {
                const cos = Math.cos(angleRad);
                const sin = Math.sin(angleRad);
                return new THREE.Vector3(
                    vector.x * cos - vector.y * sin,
                    vector.x * sin + vector.y * cos,
                    0
                );
            };

            // Curve üzerindeki toplam uzunluğu hesapla
            const curveLength = curve.getLength();

            // Her 150 birimde bir marker ekle
            const markerInterval = 150;
            let distanceAlongCurve = markerInterval; // İlk marker'ı 150 birimden sonra koy

            while (distanceAlongCurve < curveLength) {
                // Curve üzerinde bu mesafedeki noktayı ve yönü al
                const t = distanceAlongCurve / curveLength; // Normalized pozisyon (0-1)
                const pointOnCurve = curve.getPointAt(t);
                const tangent = curve.getTangentAt(t).normalize();

                // Tangent'e dik yön (normal)
                const normal = new THREE.Vector3(-tangent.y, tangent.x, 0).normalize();
                const angleRad = THREE.MathUtils.degToRad(45);
                const angledDir = rotateVector(normal, angleRad);

                const forwardOffset = tangent.clone().setLength(1.5);
                const halfLength = 2.5;

                // 2 marker: curve yönünde ileri ve geri
                const positions = [
                    pointOnCurve.clone().sub(forwardOffset),
                    pointOnCurve.clone().add(forwardOffset),
                ];

                positions.forEach((centerPoint) => {
                    const start = centerPoint.clone().add(angledDir.clone().multiplyScalar(-halfLength));
                    const end = centerPoint.clone().add(angledDir.clone().multiplyScalar(halfLength));

                    const geo = new THREE.BufferGeometry().setFromPoints([start, end]);
                    const markerColorHex = settings?.fixture?.color || '#ffffff';
                    const markerColorNum = typeof markerColorHex === 'string' ? parseInt(markerColorHex.replace('#', ''), 16) : markerColorHex;
                    const mat = new THREE.LineBasicMaterial({ color: markerColorNum });
                    const markerLine = new THREE.Line(geo, mat);
                    markerLine.userData = {
                        role: 'wireMarker',
                        parentWireId: wireId,
                        type: 'permanent',
                        isSelectable: false, // ✅ Wire marker'lar seçilemez
                        isGizmo: false // ✅ Gizmo değil
                    };
                    scene.add(markerLine);
                });

                distanceAlongCurve += markerInterval;
            }
        };

        /**
         * Spline'ı yeniden oluştur (kontrol noktaları değiştiğinde)
         */
        const rebuildSpline = (wireGroup, newControlPoints) => {
            // Wire group içindeki Line objesini bul
            let wireLine = null;
            wireGroup.traverse(obj => {
                if (obj.isLine && obj.userData?.type === 'curvedLine') {
                    wireLine = obj;
                }
            });

            if (!wireLine) return;

            // Yeni curve oluştur (gizmo pozisyonlarından)
            const curve = new THREE.CatmullRomCurve3(newControlPoints);
            const divisions = Math.max(10, newControlPoints.length * 10);
            const splinePoints = curve.getPoints(divisions);

            // Geometriyi güncelle
            wireLine.geometry.dispose();
            wireLine.geometry = new THREE.BufferGeometry().setFromPoints(splinePoints);

            // ✅ userData vertices'i de güncelle (gizmo pozisyonlarıyla)
            wireLine.userData.vertices = newControlPoints.map(v => ({ x: v.x, y: v.y, z: v.z }));
        };

        /**
         * Click handler
         */
        const onClick = (event) => {
            const mouse = getMouse(event);
            raycaster.setFromCamera(mouse, camera);
            const intersects = raycaster.intersectObjects(scene.children, true);

            // Önce fixture gizmo kontrolü
            const gizmo = intersects.find(i => i.object.name.startsWith("__fixture_control"));

            if (!isMoving.current && gizmo) {
                // Gizmo seçildi - sürükleme başlat
                selectedGizmo.current = gizmo.object;
                originalPosition.current.copy(gizmo.object.position);
                isMoving.current = true;

                // ✅ Wire marker'ları temizle (gizmo sürüklenirken)
                const wireId = selectedGizmo.current.userData.wireId;
                if (wireId) {
                    clearWireMarkers(wireId);
                }

                plane.setFromNormalAndCoplanarPoint(
                    camera.getWorldDirection(new THREE.Vector3()),
                    selectedGizmo.current.position
                );
                raycaster.ray.intersectPlane(plane, planeIntersect);
                offset.copy(planeIntersect).sub(selectedGizmo.current.position);
            } else if (isMoving.current) {
                // Gizmo sürükleme tamamlandı - Redux'ı güncelle
                const wireGroup = selectedGizmo.current.userData.wireGroup;
                const wireId = selectedGizmo.current.userData.wireId;

                // Yeni kontrol noktalarını topla
                const newControlPoints = gizmosRef.current.map(g => ({
                    x: g.position.x,
                    y: g.position.y,
                    z: g.position.z
                }));

                // Redux wireSlice'ı güncelle
                dispatch(updateWire({
                    id: wireId,
                    changes: {
                        vertices: newControlPoints
                    }
                }));

                // ✅ Wire userData zaten rebuildSpline'da güncellendi

                // ✅ Wire marker'ları yeniden oluştur
                const vector3Points = gizmosRef.current.map(g => g.position.clone());
                const curve = new THREE.CatmullRomCurve3(vector3Points);
                recreateWireMarkers(wireId, curve);

                selectedGizmo.current = null;
                isMoving.current = false;
            } else {
                // Spline wire seçimi kontrolü
                const wireHit = intersects.find(i => {
                    const isLine = i.object.isLine;
                    const isCurved = i.object.userData?.type === 'curvedLine';
                    const isFixture = i.object.userData?.function === 'wireLightingFixture';
                    const isWireMarker = i.object.userData?.role === 'wireMarker'; // ✅ Wire marker kontrolü

                    return isLine && isCurved && isFixture && !isWireMarker; // ✅ Wire marker'ları hariç tut
                });

                if (wireHit) {
                    // Spline wire'a tıklandı - gizmoları göster
                    const wireGroup = wireHit.object.parent;
                    createGizmosForWire(wireGroup);
                } else {
                    // Başka bir yere tıklandı - gizmoları gizle
                    clearGizmos();
                }
            }
        };

        /**
         * Mouse move handler
         */
        const onMouseMove = (event) => {
            if (!isMoving.current || !selectedGizmo.current) return;

            const mouse = getMouse(event);
            raycaster.setFromCamera(mouse, camera);
            raycaster.ray.intersectPlane(plane, planeIntersect);

            const newPosition = planeIntersect.clone().sub(offset);
            selectedGizmo.current.position.copy(newPosition);

            // Spline'ı yeniden oluştur
            const wireGroup = selectedGizmo.current.userData.wireGroup;
            const controlIndex = selectedGizmo.current.userData.controlIndex;

            // Yeni kontrol noktalarını topla
            const newControlPoints = [];
            gizmosRef.current.forEach(gizmo => {
                newControlPoints.push(gizmo.position.clone());
            });

            rebuildSpline(wireGroup, newControlPoints);
        };

        /**
         * ESC tuşu - iptal
         */
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (isMoving.current && selectedGizmo.current) {
                    const wireId = selectedGizmo.current.userData.wireId;

                    // Tüm gizmoları orijinal pozisyonlarına döndür
                    gizmosRef.current.forEach((gizmo, index) => {
                        if (originalControlPoints.current[index]) {
                            gizmo.position.copy(originalControlPoints.current[index]);
                        }
                    });

                    // Spline'ı eski haline döndür
                    const wireGroup = selectedGizmo.current.userData.wireGroup;
                    rebuildSpline(wireGroup, originalControlPoints.current);

                    // ✅ Wire marker'ları yeniden oluştur (orijinal spline için)
                    const curve = new THREE.CatmullRomCurve3(originalControlPoints.current);
                    recreateWireMarkers(wireId, curve);

                    selectedGizmo.current = null;
                    isMoving.current = false;
                }
            }
        };

        domElement.addEventListener("click", onClick);
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("keydown", onKeyDown);

        return () => {
            domElement.removeEventListener("click", onClick);
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("keydown", onKeyDown);
            clearGizmos();
        };
    }, [scene, camera, domElement, settings]); // ✅ settings dependency eklendi
}

export default useFixtureGizmo;
